class HeartbeatMonitor {
  constructor() {
    this.apiBaseUrl = 'https://dev-spinnaker.intaiq.com';
    this.apiKey = 'heartbeat-api-key-2024';
    
    this.pollIntervals = {
      logs: 60000,        // 1 minute
      external: 900000    // 15 minutes
    };
    
    this.globalRefreshInterval = null; // For global refresh setting
    this.intervals = {};
    this.lastCheckTimes = {
      hubspot: null,
      efax: null
    };
  }

  async makeApiCall(endpoint, options = {}) {
    try {
      // Get session hash from sessionStorage for authenticated requests
      const sessionHash = sessionStorage.getItem('session_hash');
      const userEmail = sessionStorage.getItem('user_email');
      
      console.log('Heartbeat API Call Debug:', {
        endpoint: endpoint,
        sessionHash: sessionHash ? sessionHash.substring(0, 10) + '...' : 'NO SESSION',
        userEmail: userEmail,
        apiKey: this.apiKey
      });
      
      const response = await fetch(`${this.apiBaseUrl}${endpoint}`, {
        ...options,
        headers: {
          'X-API-Key': this.apiKey,
          'Content-Type': 'application/json',
          ...(sessionHash && { 'X-Session-Hash': sessionHash }),
          ...options.headers
        }
      });

      console.log('Heartbeat API Response:', {
        status: response.status,
        ok: response.ok,
        statusText: response.statusText
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('API Error Response:', errorText);
        throw new Error(`API call failed: ${response.status} ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('API call error:', error);
      return {
        status: 'error',
        message: error.message,
        timestamp: new Date().toISOString()
      };
    }
  }

  formatTimestamp(timestamp) {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  }

  formatNextCheckTime(lastCheck, intervalMs) {
    if (!lastCheck) return 'Checking...';
    const nextCheck = new Date(lastCheck.getTime() + intervalMs);
    const now = new Date();
    const diff = Math.round((nextCheck - now) / 1000);
    
    if (diff <= 0) return 'Checking now...';
    if (diff < 60) return `${diff}s`;
    const minutes = Math.floor(diff / 60);
    const seconds = diff % 60;
    return `${minutes}m ${seconds}s`;
  }

  updateStatusBadge(elementId, status, message = '') {
    const element = document.getElementById(elementId);
    if (!element) return;

    let badgeClass = 'status-loading';
    let text = 'Unknown';

    switch (status.toLowerCase()) {
      case 'healthy':
      case 'running':
        badgeClass = 'status-healthy';
        text = 'Healthy';
        break;
      case 'degraded':
        badgeClass = 'status-degraded';
        text = 'Degraded';
        break;
      case 'unhealthy':
      case 'stopped':
      case 'error':
        badgeClass = 'status-unhealthy';
        text = 'Unhealthy';
        break;
      case 'not found':
        badgeClass = 'status-not-found';
        text = 'Not Found';
        break;
    }

    element.innerHTML = `<span class="status-badge ${badgeClass}">${text}</span>`;
    
    if (message && document.getElementById(`${elementId.replace('-status', '-message')}`)) {
      document.getElementById(`${elementId.replace('-status', '-message')}`).textContent = message;
    }
  }

  async checkDatabase() {
    const data = await this.makeApiCall('/api/v1/heartbeat/database');
    
    this.updateStatusBadge('database-status', data.status);
    
    const detailsContainer = document.getElementById('database-details');
    if (detailsContainer) {
      if (data.status === 'error') {
        detailsContainer.innerHTML = `
          <div class="alert alert-danger">
            <strong>Error:</strong> ${data.message}
          </div>
        `;
      } else {
        detailsContainer.innerHTML = `
          <div class="row">
            <div class="col-md-6">
              <p><strong>Status:</strong> <span class="badge badge-${data.status === 'healthy' ? 'success' : 'danger'}">${data.status}</span></p>
              <p><strong>Last Check:</strong> ${this.formatTimestamp(data.timestamp)}</p>
            </div>
            <div class="col-md-6">
              <p><strong>Message:</strong> ${data.message || 'No message'}</p>
              <p><strong>Components:</strong></p>
              <ul class="list-unstyled">
                ${data.components ? Object.entries(data.components).map(([key, value]) => 
                  `<li><small>${key}: ${value}</small></li>`
                ).join('') : '<li><small>No component data</small></li>'}
              </ul>
            </div>
          </div>
        `;
      }
    }
    
    document.getElementById('database-message').textContent = data.message || 'Status checked';
  }

  async checkServices() {
    const data = await this.makeApiCall('/api/v1/heartbeat/services');
    
    const summaryElement = document.getElementById('services-summary');
    if (summaryElement && data.summary) {
      summaryElement.innerHTML = `
        <span class="status-count running">${data.summary.running || 0}</span>
        <span class="status-count stopped">${data.summary.stopped || 0}</span>
        <span class="status-count not-found">${data.summary.not_found || 0}</span>
      `;
    }
    
    const detailsContainer = document.getElementById('services-details');
    if (detailsContainer) {
      if (data.status === 'error') {
        detailsContainer.innerHTML = `
          <div class="alert alert-danger">
            <strong>Error:</strong> ${data.message}
          </div>
        `;
      } else if (data.services && data.services.length > 0) {
        detailsContainer.innerHTML = `
          <div class="table-responsive">
            <table class="table table-hover">
              <thead>
                <tr>
                  <th>Service Name</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Description</th>
                  <th>Message</th>
                </tr>
              </thead>
              <tbody>
                ${data.services.map(service => `
                  <tr>
                    <td><strong>${service.name}</strong></td>
                    <td>${service.type}</td>
                    <td>
                      <span class="badge badge-${
                        service.status === 'running' ? 'success' : 
                        service.status === 'stopped' ? 'danger' : 'warning'
                      }">${service.status}</span>
                    </td>
                    <td><small>${service.description || '-'}</small></td>
                    <td><small>${service.message || '-'}</small></td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
          </div>
        `;
      } else {
        detailsContainer.innerHTML = '<p class="text-muted">No services data available</p>';
      }
    }
  }

  async checkHubSpot() {
    const data = await this.makeApiCall('/api/v1/heartbeat/external/hubspot');
    this.lastCheckTimes.hubspot = new Date();
    
    this.updateStatusBadge('hubspot-status', data.status);
    
    const detailsContainer = document.getElementById('hubspot-details');
    if (detailsContainer) {
      if (data.status === 'error') {
        detailsContainer.innerHTML = `
          <div class="alert alert-danger">
            <strong>Error:</strong> ${data.message}
          </div>
        `;
      } else if (data.organizations && data.organizations.length > 0) {
        detailsContainer.innerHTML = `
          <div class="row mb-3">
            <div class="col-md-12">
              <p><strong>Overall Status:</strong> <span class="badge badge-${
                data.status === 'healthy' ? 'success' : 
                data.status === 'degraded' ? 'warning' : 'danger'
              }">${data.status}</span></p>
              <p><strong>Last Check:</strong> ${this.formatTimestamp(data.timestamp)}</p>
            </div>
          </div>
          <div class="table-responsive">
            <table class="table table-hover">
              <thead>
                <tr>
                  <th>Organization</th>
                  <th>ID</th>
                  <th>Status</th>
                  <th>Response Time</th>
                  <th>Message</th>
                </tr>
              </thead>
              <tbody>
                ${data.organizations.map(org => `
                  <tr>
                    <td><strong>${org.organization_name || 'Unknown'}</strong></td>
                    <td><code>${org.organization_id}</code></td>
                    <td>
                      <span class="badge badge-${
                        org.status === 'healthy' ? 'success' : 'danger'
                      }">${org.status}</span>
                    </td>
                    <td>${org.response_time_ms ? `${org.response_time_ms.toFixed(1)}ms` : '-'}</td>
                    <td><small>${org.message || '-'}</small></td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
          </div>
        `;
      } else {
        detailsContainer.innerHTML = '<p class="text-muted">No HubSpot data available</p>';
      }
    }
    
    this.updateNextCheckTime('hubspot');
  }

  async checkEfax() {
    const data = await this.makeApiCall('/api/v1/heartbeat/external/efax');
    this.lastCheckTimes.efax = new Date();
    
    this.updateStatusBadge('efax-status', data.status);
    
    const detailsContainer = document.getElementById('efax-details');
    if (detailsContainer) {
      if (data.status === 'error') {
        detailsContainer.innerHTML = `
          <div class="alert alert-danger">
            <strong>Error:</strong> ${data.message}
          </div>
        `;
      } else if (data.organizations && data.organizations.length > 0) {
        detailsContainer.innerHTML = `
          <div class="row mb-3">
            <div class="col-md-12">
              <p><strong>Overall Status:</strong> <span class="badge badge-${
                data.status === 'healthy' ? 'success' : 
                data.status === 'degraded' ? 'warning' : 'danger'
              }">${data.status}</span></p>
              <p><strong>Last Check:</strong> ${this.formatTimestamp(data.timestamp)}</p>
            </div>
          </div>
          <div class="table-responsive">
            <table class="table table-hover">
              <thead>
                <tr>
                  <th>Organization</th>
                  <th>ID</th>
                  <th>Status</th>
                  <th>Response Time</th>
                  <th>Message</th>
                </tr>
              </thead>
              <tbody>
                ${data.organizations.map(org => `
                  <tr>
                    <td><strong>${org.organization_name || 'Unknown'}</strong></td>
                    <td><code>${org.organization_id}</code></td>
                    <td>
                      <span class="badge badge-${
                        org.status === 'healthy' ? 'success' : 'danger'
                      }">${org.status}</span>
                    </td>
                    <td>${org.response_time_ms ? `${org.response_time_ms.toFixed(1)}ms` : '-'}</td>
                    <td><small>${org.message || '-'}</small></td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
          </div>
        `;
      } else {
        detailsContainer.innerHTML = '<p class="text-muted">No eFax data available</p>';
      }
    }
    
    this.updateNextCheckTime('efax');
  }

  async fetchLogs() {
    const serviceFilter = document.getElementById('log-service-filter').value;
    const limit = document.getElementById('log-limit').value || 50;
    
    let endpoint = `/api/v1/heartbeat/logs?limit=${limit}`;
    if (serviceFilter) {
      endpoint += `&service=${encodeURIComponent(serviceFilter)}`;
    }
    
    console.log('Fetching logs from:', endpoint);
    const data = await this.makeApiCall(endpoint);
    console.log('Received logs:', data.logs ? data.logs.length : 0);
    
    const logsContainer = document.getElementById('logs-container');
    if (logsContainer) {
      if (data.status === 'error') {
        logsContainer.innerHTML = `
          <div class="alert alert-danger">
            <strong>Error:</strong> ${data.message}
          </div>
        `;
      } else if (data.logs && data.logs.length > 0) {
        // Build the logs HTML with inline styles for immediate white background
        const logsHtml = data.logs.map(log => {
          const levelClass = log.level.toLowerCase();
          const levelColor = log.level === 'ERROR' ? '#dc3545' : 
                           log.level === 'WARNING' ? '#ffc107' : 
                           log.level === 'INFO' ? '#17a2b8' : '#6c757d';
          
          return `
            <div style="margin-bottom: 8px; font-family: 'Courier New', Courier, monospace; font-size: 13px; line-height: 1.4;">
              <span style="color: #666;">[${this.formatTimestamp(log.timestamp)}]</span>
              <span style="background: ${levelColor}; color: white; padding: 1px 4px; border-radius: 2px; font-size: 11px; font-weight: bold; margin: 0 4px;">${log.level}</span>
              <span style="color: #0066cc; font-weight: bold;">[${log.service}]</span>
              <span style="color: #000;">${log.message}</span>
              ${log.metadata ? `
                <div style="margin-top: 4px; padding-left: 20px; color: #666; font-size: 12px;">
                  Metadata: ${JSON.stringify(log.metadata)}
                </div>
              ` : ''}
            </div>
          `;
        }).join('');
        
        logsContainer.innerHTML = `
          <div style="padding: 12px 16px; border-bottom: 1px solid #dee2e6; background-color: #f8f9fa;">
            <small class="text-muted">Showing ${data.logs.length} of ${data.total_count || data.logs.length} logs</small>
            ${data.filtered_service ? `<small class="text-muted ml-2">| Filtered: ${data.filtered_service}</small>` : ''}
          </div>
          <div style="height: 450px; overflow-y: auto; padding: 16px; background-color: #ffffff; color: #000000; border: 1px solid #dee2e6;">
            ${logsHtml}
          </div>
        `;
      } else {
        logsContainer.innerHTML = `
          <div style="padding: 20px; text-align: center; color: #6c757d;">
            No logs available
          </div>
        `;
      }
    }
  }

  updateNextCheckTime(service) {
    const elementId = `${service}-message`;
    const element = document.getElementById(elementId);
    if (!element) return;
    
    const updateTime = () => {
      const nextCheckTime = this.formatNextCheckTime(
        this.lastCheckTimes[service],
        this.pollIntervals.external
      );
      element.textContent = `Next check: ${nextCheckTime}`;
    };
    
    updateTime();
    
    if (this.intervals[`${service}_timer`]) {
      clearInterval(this.intervals[`${service}_timer`]);
    }
    this.intervals[`${service}_timer`] = setInterval(updateTime, 1000);
  }

  startPolling() {
    // Initial checks
    this.checkDatabase();
    this.checkServices();
    this.checkHubSpot();
    this.checkEfax();
    this.fetchLogs();
    
    // Poll logs every minute
    this.intervals.logs = setInterval(() => {
      this.fetchLogs();
    }, this.pollIntervals.logs);
    
    // Poll external services every 15 minutes
    this.intervals.hubspot = setInterval(() => {
      this.checkHubSpot();
    }, this.pollIntervals.external);
    
    this.intervals.efax = setInterval(() => {
      this.checkEfax();
    }, this.pollIntervals.external);
    
    // Poll database and services every 2 minutes
    this.intervals.database = setInterval(() => {
      this.checkDatabase();
    }, 120000);
    
    this.intervals.services = setInterval(() => {
      this.checkServices();
    }, 120000);
  }

  stopPolling() {
    Object.keys(this.intervals).forEach(key => {
      clearInterval(this.intervals[key]);
    });
    this.intervals = {};
  }

  setGlobalRefreshInterval(minutes) {
    // Clear existing global refresh if any
    if (this.intervals.globalRefresh) {
      clearInterval(this.intervals.globalRefresh);
      delete this.intervals.globalRefresh;
    }

    if (!minutes || minutes === 0) {
      console.log('Global refresh disabled');
      return;
    }

    const intervalMs = minutes * 60 * 1000;
    console.log(`Setting global refresh interval to ${minutes} minute(s)`);
    
    // Set up global refresh that calls all check methods
    this.intervals.globalRefresh = setInterval(() => {
      console.log('Performing global refresh...');
      this.checkDatabase();
      this.checkServices();
      this.checkHubSpot();
      this.checkEfax();
      this.fetchLogs();
    }, intervalMs);
    
    // Also perform an immediate refresh
    this.performGlobalRefresh();
  }

  performGlobalRefresh() {
    console.log('Refreshing all services...');
    this.checkDatabase();
    this.checkServices();
    this.checkHubSpot();
    this.checkEfax();
    this.fetchLogs();
  }

  init() {
    console.log('Heartbeat Monitor initialized');
    console.log('API Base URL:', this.apiBaseUrl);
    console.log('Using session-based authentication');
    console.log('Starting polling intervals - Logs: 1min, External Services: 15min');
    
    this.startPolling();
    
    // Stop polling when page is hidden
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.stopPolling();
      } else {
        this.startPolling();
      }
    });
  }
}

// Initialize the monitor when DOM is ready and expose globally
const heartbeatMonitor = new HeartbeatMonitor();
window.heartbeatMonitor = heartbeatMonitor; // Expose globally for onclick handlers

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => heartbeatMonitor.init());
} else {
  heartbeatMonitor.init();
}